import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import './styles.css'
import { ThemeProvider } from './context/ThemeContext'
import { FlagsProvider } from './context/FlagsContext'
import { ExperimentsProvider } from './context/ExperimentsContext'
import { ToastProvider } from './components/ui/Toast'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary'
import { setupAutoRefreshOnLoad } from './lib/oidc'

// Minimal client error reporting hook (optional): send to endpoint if provided
const clientErrorEndpoint = import.meta.env.VITE_CLIENT_ERROR_ENDPOINT as string | undefined
if (clientErrorEndpoint) {
  window.addEventListener('error', (e) => {
    const payload = {
      type: 'error',
      message: e.message,
      stack: e.error?.stack,
      source: e.filename,
      line: e.lineno,
      col: e.colno,
    }
    fetch(clientErrorEndpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      .catch((error) => console.warn('Failed to report window error', error))
  })
  window.addEventListener('unhandledrejection', (e) => {
    const payload = { type: 'rejection', reason: String(e.reason) }
    fetch(clientErrorEndpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      .catch((error) => console.warn('Failed to report unhandled rejection', error))
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount) => failureCount < 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false
    },
    mutations: {
      retry: 0
    }
  }
})

// Set up proactive token refresh if tokens already exist
try {
  setupAutoRefreshOnLoad()
} catch (error) {
  console.warn('Failed to initialize token auto-refresh', error)
}

// Dev-only accessibility checks (console warnings)
if (import.meta.env.DEV) {
  import('@axe-core/react')
    .then(({ default: axe }) => {
      // small delay so React is ready
      axe(React, ReactDOM, 1000)
    })
    .catch((error) => console.warn('Failed to load axe-core for accessibility checks', error))
}

// Sentry: dynamically initialize if DSN is provided (no build-time dependency required)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
if (sentryDsn) {
  const sentryModule = '@sentry/react'
  import(/* @vite-ignore */ sentryModule)
    .then((Sentry) => {
      Sentry.init({
        dsn: sentryDsn,
        integrations: [
          (Sentry as any).browserTracingIntegration?.(),
          // capture fetch/XHR failures as breadcrumbs + errors
          (Sentry as any).httpClientIntegration?.({
            failedRequestStatusCodes: [[400, 599]],
            breadcrumbs: true
          }),
          (Sentry as any).replayIntegration?.({ maskAllText: false, blockAllMedia: true })
        ].filter(Boolean),
        tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1),
        replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? 0.0),
        replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? 1.0),
        release: (import.meta.env.VITE_APP_RELEASE as string | undefined) || undefined,
        environment: import.meta.env.MODE
      })
    })
    .catch((error) => console.warn('Failed to initialize Sentry', error))
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <FlagsProvider>
        <ExperimentsProvider>
          <ToastProvider>
            <QueryClientProvider client={queryClient}>
              <GlobalErrorBoundary>
                <BrowserRouter basename={(import.meta.env.VITE_BASE_PATH as string) || '/'}>
                  <App />
                </BrowserRouter>
              </GlobalErrorBoundary>
            </QueryClientProvider>
          </ToastProvider>
        </ExperimentsProvider>
      </FlagsProvider>
    </ThemeProvider>
  </React.StrictMode>
)
